import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { useEffect, useState, lazy, Suspense } from 'react'
import { supabase } from './lib/supabase'
import ErrorBoundary from './components/ErrorBoundary'
import './index.css'

import Login from './pages/Login'



import Sidebar from './components/Sidebar'
import TopBar from './components/TopBar'
import CommandPalette from './components/CommandPalette'
import ShortcutsModal from './components/ShortcutsModal'
import FloatingChat from './components/FloatingChat'
import ClientTourHost from './components/ClientTourHost'
import { ToastProvider } from './components/Toast'
import { clientPath } from './lib/recordLinks'

// While a page's chunk is in flight. Matches the existing auth loading screen,
// so a split boundary looks like the app thinking rather than a new kind of wait.
function PageFallback() {
  return (
    <div className="loading-screen">
      <div className="spinner"></div>
    </div>
  )
}

// Every page below is its own chunk.
//
// The bundle was 1.06 MB gzipped with no splitting at all, so a client opening
// an invoice link on their phone downloaded all seventy-odd admin pages plus
// xlsx, jspdf, pdfjs-dist, mammoth, docx and recharts to read one number. Login
// stays eager because it is the first thing an unauthenticated visitor sees.
const Dashboard = lazy(() => import('./pages/Dashboard'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const Projects = lazy(() => import('./pages/Projects'))
const ProjectSop = lazy(() => import('./pages/ProjectSop'))
const Files = lazy(() => import('./pages/Files'))
const Invoices = lazy(() => import('./pages/Invoices'))
const Approvals = lazy(() => import('./pages/Approvals'))
const Requests = lazy(() => import('./pages/Requests'))
const Proposals = lazy(() => import('./pages/Proposals'))
const ClientProposals = lazy(() => import('./pages/ClientProposals'))
const ProposalView = lazy(() => import('./pages/ProposalView'))
const Contracts = lazy(() => import('./pages/Contracts'))
const ContractView = lazy(() => import('./pages/ContractView'))
const ClientAnalytics = lazy(() => import('./pages/ClientAnalytics'))
const ContractorDashboard = lazy(() => import('./pages/ContractorDashboard'))
const ContractorProjects = lazy(() => import('./pages/ContractorProjects'))
const ContractorProject = lazy(() => import('./pages/ContractorProject'))
const ContractorTime = lazy(() => import('./pages/ContractorTime'))
const ContractorAgreements = lazy(() => import('./pages/ContractorAgreements'))
const ContractorEarnings = lazy(() => import('./pages/ContractorEarnings'))
const AdminContractors = lazy(() => import('./pages/AdminContractors'))
const AdminContractorReviews = lazy(() => import('./pages/AdminContractorReviews'))
const ContractorReviews = lazy(() => import('./pages/ContractorReviews'))
const AdminTaxForms = lazy(() => import('./pages/AdminTaxForms'))
const ContractorW9 = lazy(() => import('./pages/ContractorW9'))
const ClientBrief = lazy(() => import('./pages/ClientBrief'))
const ClientMessages = lazy(() => import('./pages/ClientMessages'))
const QuoteRequest = lazy(() => import('./pages/QuoteRequest'))
const AdminLeads = lazy(() => import('./pages/AdminLeads'))
const AdminPortalActivity = lazy(() => import('./pages/AdminPortalActivity'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const AdminClients = lazy(() => import('./pages/AdminClients'))
const AdminOnboarding = lazy(() => import('./pages/AdminOnboarding'))
const AdminProjects = lazy(() => import('./pages/AdminProjects'))
const AdminApprovals = lazy(() => import('./pages/AdminApprovals'))
const AdminFiles = lazy(() => import('./pages/AdminFiles'))
const AdminInvoices = lazy(() => import('./pages/AdminInvoices'))
const AdminMessages = lazy(() => import('./pages/AdminMessages'))
const AdminRevisions = lazy(() => import('./pages/AdminRevisions'))
const AdminTemplates = lazy(() => import('./pages/AdminTemplates'))
const AdminReports = lazy(() => import('./pages/AdminReports'))
const AdminMetrics = lazy(() => import('./pages/AdminMetrics'))
const AdminNetworking = lazy(() => import('./pages/AdminNetworking'))
const AdminTimeReview = lazy(() => import('./pages/AdminTimeReview'))
const AdminCommunications = lazy(() => import('./pages/AdminCommunications'))
const AdminReferrals = lazy(() => import('./pages/AdminReferrals'))
const AdminSocialCalendar = lazy(() => import('./pages/AdminSocialCalendar'))
const SocialCalendarShare = lazy(() => import('./pages/SocialCalendarShare'))
const AdminSchedule = lazy(() => import('./pages/AdminSchedule'))
const Analytics = lazy(() => import('./pages/Analytics'))
const AdminClientHealth = lazy(() => import('./pages/AdminClientHealth'))
const AdminTimeTracking = lazy(() => import('./pages/AdminTimeTracking'))
const InvoiceView = lazy(() => import('./pages/InvoiceView'))
const AdminCMS = lazy(() => import('./pages/AdminCMS'))
const AdminExpenses = lazy(() => import('./pages/AdminExpenses'))
const AdminContentStudio = lazy(() => import('./pages/AdminContentStudio'))
const AdminBrandStrategy = lazy(() => import('./pages/AdminBrandStrategy'))
const BrandStrategyView = lazy(() => import('./pages/BrandStrategyView'))
const AdminProfitability = lazy(() => import('./pages/AdminProfitability'))
const AdminBrandAssets = lazy(() => import('./pages/AdminBrandAssets'))
const AdminPortfolioMedia = lazy(() => import('./pages/AdminPortfolioMedia'))
const AdminPortfolioMediaDetail = lazy(() => import('./pages/AdminPortfolioMediaDetail'))
const BrandAssets = lazy(() => import('./pages/BrandAssets'))
const Vault = lazy(() => import('./pages/Vault'))
const Settings = lazy(() => import('./pages/Settings'))
const AdminBids = lazy(() => import('./pages/AdminBids'))
const AdminRfpInbox = lazy(() => import('./pages/AdminRfpInbox'))
const BidWorkspace = lazy(() => import('./pages/BidWorkspace'))
const DocumentStudio = lazy(() => import('./pages/DocumentStudio'))
const AdminPriceList = lazy(() => import('./pages/AdminPriceList'))
const AdminProjectTemplates = lazy(() => import('./pages/AdminProjectTemplates'))
const ClientForms = lazy(() => import('./pages/ClientForms'))
const ClientFormFill = lazy(() => import('./pages/ClientFormFill'))
const AdminBidPipeline = lazy(() => import('./pages/AdminBidPipeline'))
const BidView = lazy(() => import('./pages/BidView'))
const AdminForecast = lazy(() => import('./pages/AdminForecast'))
const AdminBooks = lazy(() => import('./pages/AdminBooks'))
const AdminMeetings = lazy(() => import('./pages/AdminMeetings'))
const ClientMeetings = lazy(() => import('./pages/ClientMeetings'))
const AdminRetainers = lazy(() => import('./pages/AdminRetainers'))
const AdminAutomations = lazy(() => import('./pages/AdminAutomations'))
const SurveyView = lazy(() => import('./pages/SurveyView'))
const MoneyOverview = lazy(() => import('./pages/MoneyOverview'))
const ClientsOverview = lazy(() => import('./pages/ClientsOverview'))
const SalesOverview = lazy(() => import('./pages/SalesOverview'))
const InsightsOverview = lazy(() => import('./pages/InsightsOverview'))
const AdminWebsiteReviews = lazy(() => import('./pages/AdminWebsiteReviews'))
const WebsiteReviews = lazy(() => import('./pages/WebsiteReviews'))
const WebsiteReviewShare = lazy(() => import('./pages/WebsiteReviewShare'))
const DocumentReviewShare = lazy(() => import('./pages/DocumentReviewShare'))
const VideoReviewShare = lazy(() => import('./pages/VideoReviewShare'))
const AdminDocumentReviews = lazy(() => import('./pages/AdminDocumentReviews'))
const ReviewsInbox = lazy(() => import('./pages/ReviewsInbox'))
const ReviewWebWorkspace = lazy(() => import('./pages/ReviewWebWorkspace'))
const ReviewDocWorkspace = lazy(() => import('./pages/ReviewDocWorkspace'))
const ReviewVideoWorkspace = lazy(() => import('./pages/ReviewVideoWorkspace'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const ContractorSetup = lazy(() => import('./pages/ContractorSetup'))
const ContractorLogin = lazy(() => import('./pages/ContractorLogin'))

function PendingApproval() {
  const { signOut, client } = useAuth()
  return (
    <div style={{ minHeight: '100vh', background: 'var(--off)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
            <circle cx="14" cy="14" r="12" stroke="#D97706" strokeWidth="1.8"/>
            <path d="M14 8v7" stroke="#D97706" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="14" cy="19" r="1.2" fill="#D97706"/>
          </svg>
        </div>
        <div style={{ fontFamily: 'var(--fd)', fontSize: 24, fontWeight: 700, color: 'var(--navy)', marginBottom: 12 }}>
          Pending Approval
        </div>
        <div style={{ fontFamily: 'var(--fb)', fontSize: 14, color: 'var(--mut)', lineHeight: 1.7, marginBottom: 8 }}>
          Hi {client?.name?.split(' ')[0] || 'there'}, your account is under review. You'll get an email once you've been granted access.
        </div>
        <div style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--hint)', marginBottom: 32 }}>
          Questions? <a href="mailto:efren@abstract-creative.com" style={{ color: 'var(--red)' }}>efren@abstract-creative.com</a>
        </div>
        <button onClick={signOut} style={{ fontFamily: 'var(--fu)', fontSize: 12, color: 'var(--mut)', background: 'none', border: '1px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '8px 18px', cursor: 'pointer' }}>
          Sign out
        </button>
      </div>
    </div>
  )
}

function ClientsAlias() {
  const { search } = useLocation()
  return <Navigate to={`/admin${search}`} replace />
}

function ProtectedRoute({ children }) {
  const { user, isPending, loading } = useAuth()
  if (loading) return (
    <div className="loading-screen">
      <div className="spinner"></div>
      <span style={{ color: 'var(--neutral-mid)', fontSize: 13 }}>Loading your portal...</span>
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  if (isPending) return <PendingApproval />
  return children
}

function AdminRoute({ children }) {
  const { user, isAdmin, isPreview, loading } = useAuth()
  if (loading) return (
    <div className="loading-screen">
      <div className="spinner"></div>
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  if (!isAdmin || isPreview) return <Navigate to="/dashboard" replace />
  return children
}

// Contractor pages: a signed-in contractor, or the studio previewing one.
function ContractorRoute({ children }) {
  const { user, isContractor, isPreview, previewContractor, loading } = useAuth()
  if (loading) return (
    <div className="loading-screen">
      <div className="spinner"></div>
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  if (!isContractor && !(isPreview && previewContractor)) return <Navigate to="/dashboard" replace />
  return children
}

function PortalLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [cmdOpen, setCmdOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const { isPreview, previewClient, previewContractor, exitPreview, isAdmin } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    // "G then a letter" goes to a section. The shortcuts sheet listed these
    // with nothing behind them. The second key has to follow within a second,
    // and neither counts while typing in a field. A key something else already
    // took (the + New menu picks its items by letter while it is open) is left
    // alone, so G then P in that menu does not also leave the page. "[" is the
    // sidebar's own, and no letter here uses it.
    const GO = { h: '/dashboard', p: '/projects', i: '/invoices', c: '/admin', m: '/messages', t: '/time', l: '/leads', r: '/proposals', v: '/reviews-inbox', n: '/networking' }
    let pendingG = 0
    function typing(el) {
      return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el?.tagName) || el?.isContentEditable
    }
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen(o => !o); return }
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return
      if (e.key === '?') { e.preventDefault(); setShortcutsOpen(o => !o); return }
      if (!isAdmin || isPreview) return
      const key = e.key.toLowerCase()
      if (pendingG && Date.now() - pendingG < 1000 && GO[key]) {
        e.preventDefault(); pendingG = 0; navigate(GO[key]); return
      }
      pendingG = key === 'g' ? Date.now() : 0
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isAdmin, isPreview, navigate])
  const previewName = previewClient?.name || previewContractor?.name || 'Unknown'
  const previewType = previewClient ? 'Client' : 'Contractor'

  return (
    <div className="portal-layout">
      <a href="#main-content" className="skip-link">Skip to content</a>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="main-content">
        {isPreview && (
          <div style={{
            background: '#D13833', color: '#fff', padding: '9px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontFamily: 'var(--fu)', fontSize: 12, fontWeight: 600, gap: 12, flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="5" r="2.5" stroke="white" strokeWidth="1.3"/>
                <path d="M2 12c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
              Previewing as <strong style={{ marginLeft: 4 }}>{previewName}</strong>
              <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em' }}>
                {previewType.toUpperCase()}
              </span>
            </div>
            <button
              // Back to the record the preview was opened from, not the dashboard.
              onClick={() => {
                const back = previewClient ? clientPath(previewClient.id) : previewContractor ? '/contractors' : '/dashboard'
                exitPreview(); window.location.href = back
              }}
              style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', borderRadius: 6, padding: '4px 13px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--fu)', whiteSpace: 'nowrap' }}
            >
              ← Exit Preview
            </button>
          </div>
        )}
        <TopBar onMenuClick={() => setSidebarOpen(o => !o)} sidebarOpen={sidebarOpen} onCmdK={() => setCmdOpen(true)} />
        <main id="main-content" tabIndex={-1} className="page-body" style={{ outline: 'none' }}><ErrorBoundary>{children}</ErrorBoundary></main>
      </div>
      {cmdOpen && <CommandPalette onClose={() => setCmdOpen(false)} />}
      {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}
      <FloatingChat />
      <ClientTourHost />
    </div>
  )
}

function BuilderRedirect() {
  const { id } = useParams()
  return <Navigate to={`/proposals/${id}/studio`} replace />
}

function AppRoutes() {
  const { user, isAdmin, isContractor, isPreview, previewContractor, loading } = useAuth()
  const viewAsAdmin = isAdmin && !isPreview
  const viewAsContractor = isContractor || (isPreview && !!previewContractor)

  // Allow these pages to render immediately without waiting for auth to load
  const bypassAuth = ['/reset-password', '/contractor-setup', '/contractor-login']
  if (bypassAuth.includes(window.location.pathname)) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/reset-password"    element={<ResetPassword />} />
          <Route path="/contractor-setup"  element={<ContractorSetup />} />
          <Route path="/contractor-login"  element={<ContractorLogin />} />
        </Routes>
      </Suspense>
    )
  }

  if (loading) return (
    <div className="loading-screen">
      <div className="spinner"></div>
    </div>
  )

  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/login"              element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/contractor-login"   element={user ? <Navigate to="/dashboard" replace /> : <ContractorLogin />} />
      <Route path="/contractor-setup"   element={<ContractorSetup />} />
      <Route path="/reset-password"     element={<ResetPassword />} />

      {/* Dashboard — admin vs contractor vs client */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminDashboard /> : viewAsContractor ? <ContractorDashboard /> : <Dashboard />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Onboarding — admin vs client (contractors skip) */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminOnboarding /> : <Onboarding />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Projects — admin vs contractor vs client */}
      <Route path="/projects" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminProjects /> : viewAsContractor ? <ContractorProjects /> : <Projects />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/project-sop/:projectId" element={
        <ProtectedRoute>
          <PortalLayout>
            <ProjectSop />
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Files — admin vs client */}
      <Route path="/files" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminFiles /> : <Files />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Invoices — admin vs client */}
      <Route path="/invoices" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminInvoices /> : <Invoices />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Approvals — admin vs client */}
      <Route path="/approvals" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminApprovals /> : <Approvals />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Portal requests — admin vs client */}
      <Route path="/requests" element={
        <ProtectedRoute>
          <PortalLayout>
            <Requests />
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Analytics — client only */}
      <Route path="/analytics" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <Analytics /> : <ClientAnalytics />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Admin-only routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <PortalLayout><AdminClients /></PortalLayout>
        </AdminRoute>
      } />

      <Route path="/settings" element={
        <AdminRoute>
          <PortalLayout><Settings /></PortalLayout>
        </AdminRoute>
      } />

      <Route path="/contractors" element={
        <AdminRoute>
          <PortalLayout><AdminContractors /></PortalLayout>
        </AdminRoute>
      } />

      <Route path="/reviews" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminContractorReviews /> : viewAsContractor ? <ContractorReviews /> : <Navigate to="/dashboard" replace />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/document-reviews" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminDocumentReviews /> : <Navigate to="/dashboard" replace />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/tax-forms" element={
        <AdminRoute>
          <PortalLayout><AdminTaxForms /></PortalLayout>
        </AdminRoute>
      } />

      {/* Contractor-only pages */}
      <Route path="/my-projects" element={
        <ContractorRoute><PortalLayout><ContractorProjects /></PortalLayout></ContractorRoute>
      } />
      <Route path="/my-projects/:projectId" element={
        <ContractorRoute><PortalLayout><ContractorProject /></PortalLayout></ContractorRoute>
      } />
      <Route path="/my-time" element={
        <ContractorRoute><PortalLayout><ContractorTime /></PortalLayout></ContractorRoute>
      } />
      <Route path="/my-agreements" element={
        <ContractorRoute><PortalLayout><ContractorAgreements /></PortalLayout></ContractorRoute>
      } />
      <Route path="/earnings" element={
        <ContractorRoute><PortalLayout><ContractorEarnings /></PortalLayout></ContractorRoute>
      } />

      <Route path="/w9" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsContractor ? <ContractorW9 /> : <Navigate to="/dashboard" replace />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/brief" element={
        <ProtectedRoute>
          <PortalLayout><ClientBrief /></PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/social-calendar" element={
        <AdminRoute>
          <PortalLayout><AdminSocialCalendar /></PortalLayout>
        </AdminRoute>
      } />

      <Route path="/schedule" element={
        <AdminRoute>
          <PortalLayout><AdminSchedule /></PortalLayout>
        </AdminRoute>
      } />

      {/* Shared routes */}
      <Route path="/proposals" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <Proposals /> : <ClientProposals />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/p/:token" element={<ProposalView />} />
      <Route path="/sc/:token" element={<SocialCalendarShare />} />
      {/* The old proposal builder is retired; every proposal is written in the
          studio. Kept as a redirect so an old bookmark still lands somewhere. */}
      <Route path="/proposals/builder/:id" element={<BuilderRedirect />} />
      <Route path="/inv/:token" element={<InvoiceView />} />

      <Route path="/contracts" element={
        <AdminRoute>
          <PortalLayout><Contracts /></PortalLayout>
        </AdminRoute>
      } />

      <Route path="/contract/:token" element={<ContractView />} />

      <Route path="/messages" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminMessages /> : <ClientMessages />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/revisions" element={
        <AdminRoute><PortalLayout><AdminRevisions /></PortalLayout></AdminRoute>
      } />

      <Route path="/templates" element={
        <AdminRoute><PortalLayout><AdminTemplates /></PortalLayout></AdminRoute>
      } />

      <Route path="/time-review" element={
        <AdminRoute><PortalLayout><AdminTimeReview /></PortalLayout></AdminRoute>
      } />

      <Route path="/networking" element={
        <AdminRoute><PortalLayout><AdminNetworking /></PortalLayout></AdminRoute>
      } />

      <Route path="/metrics" element={
        <AdminRoute><PortalLayout><AdminMetrics /></PortalLayout></AdminRoute>
      } />

      <Route path="/reports" element={
        <AdminRoute><PortalLayout><AdminReports /></PortalLayout></AdminRoute>
      } />
      <Route path="/communications" element={
        <AdminRoute><PortalLayout><AdminCommunications /></PortalLayout></AdminRoute>
      } />

      <Route path="/referrals" element={
        <AdminRoute><PortalLayout><AdminReferrals /></PortalLayout></AdminRoute>
      } />


      {/* Space overviews — the first page of Money, Clients, Sales and Insights. */}
      <Route path="/money" element={
        <AdminRoute><PortalLayout><MoneyOverview /></PortalLayout></AdminRoute>
      } />
      <Route path="/clients-overview" element={
        <AdminRoute><PortalLayout><ClientsOverview /></PortalLayout></AdminRoute>
      } />
      <Route path="/sales" element={
        <AdminRoute><PortalLayout><SalesOverview /></PortalLayout></AdminRoute>
      } />
      <Route path="/insights" element={
        <AdminRoute><PortalLayout><InsightsOverview /></PortalLayout></AdminRoute>
      } />

      <Route path="/health" element={
        <AdminRoute><PortalLayout><AdminClientHealth /></PortalLayout></AdminRoute>
      } />

      <Route path="/nudges" element={
        <Navigate to="/health?tab=nudges" replace />
      } />

      <Route path="/time" element={
        <AdminRoute><PortalLayout><AdminTimeTracking /></PortalLayout></AdminRoute>
      } />

      <Route path="/expenses" element={
        <AdminRoute><PortalLayout><AdminExpenses /></PortalLayout></AdminRoute>
      } />

      <Route path="/cms" element={
        <AdminRoute><PortalLayout><AdminCMS /></PortalLayout></AdminRoute>
      } />

      <Route path="/content-studio" element={
        <AdminRoute><PortalLayout><AdminContentStudio /></PortalLayout></AdminRoute>
      } />

      <Route path="/brand-strategy" element={
        <AdminRoute><PortalLayout><AdminBrandStrategy /></PortalLayout></AdminRoute>
      } />

      <Route path="/bs/:token" element={<BrandStrategyView />} />

      <Route path="/bids" element={
        <AdminRoute><PortalLayout><AdminBids /></PortalLayout></AdminRoute>
      } />

      <Route path="/bid-pipeline" element={
        <AdminRoute><PortalLayout><AdminBidPipeline /></PortalLayout></AdminRoute>
      } />

      {/* One editor. The route names the record; the studio reads it through
          an adapter into the same document either way. */}
      <Route path="/bids/:id/studio" element={
        <AdminRoute><PortalLayout><DocumentStudio /></PortalLayout></AdminRoute>
      } />

      <Route path="/proposals/:id/studio" element={
        <AdminRoute><PortalLayout><DocumentStudio /></PortalLayout></AdminRoute>
      } />

      <Route path="/bids/:id" element={
        <AdminRoute><PortalLayout><BidWorkspace /></PortalLayout></AdminRoute>
      } />

      <Route path="/project-templates" element={
        <AdminRoute><PortalLayout><AdminProjectTemplates /></PortalLayout></AdminRoute>
      } />

      {/* Questionnaires sent to a client. The studio builds them on Project
          Templates and reads the answers on the project. */}
      <Route path="/forms" element={
        <ProtectedRoute>
          <PortalLayout>{viewAsAdmin ? <Navigate to="/project-templates" replace /> : <ClientForms />}</PortalLayout>
        </ProtectedRoute>
      } />
      <Route path="/forms/:id" element={
        <ProtectedRoute><PortalLayout><ClientFormFill /></PortalLayout></ProtectedRoute>
      } />

      <Route path="/price-list" element={
        <AdminRoute><PortalLayout><AdminPriceList /></PortalLayout></AdminRoute>
      } />

      <Route path="/rfp-inbox" element={
        <AdminRoute><PortalLayout><AdminRfpInbox /></PortalLayout></AdminRoute>
      } />

      <Route path="/b/:token" element={<BidView />} />

      <Route path="/quote" element={<QuoteRequest />} />

      <Route path="/leads" element={
        <AdminRoute><PortalLayout><AdminLeads /></PortalLayout></AdminRoute>
      } />

      <Route path="/profitability" element={
        <AdminRoute><PortalLayout><AdminProfitability /></PortalLayout></AdminRoute>
      } />

      <Route path="/portfolio-media" element={
        <AdminRoute><PortalLayout><AdminPortfolioMedia /></PortalLayout></AdminRoute>
      } />

      <Route path="/portfolio-media/:id" element={
        <AdminRoute><PortalLayout><AdminPortfolioMediaDetail /></PortalLayout></AdminRoute>
      } />

      <Route path="/brand-assets" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminBrandAssets /> : <BrandAssets />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/vault" element={
        <ProtectedRoute>
          <PortalLayout>
            <Vault />
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/portal-activity" element={
        <AdminRoute><PortalLayout><AdminPortalActivity /></PortalLayout></AdminRoute>
      } />

      <Route path="/forecast" element={
        <AdminRoute><PortalLayout><AdminForecast /></PortalLayout></AdminRoute>
      } />

      <Route path="/books" element={
        <AdminRoute><PortalLayout><AdminBooks /></PortalLayout></AdminRoute>
      } />

      <Route path="/meetings" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminMeetings /> : <ClientMeetings />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      <Route path="/retainers" element={
        <AdminRoute><PortalLayout><AdminRetainers /></PortalLayout></AdminRoute>
      } />

      <Route path="/surveys" element={
        <Navigate to="/health?tab=surveys" replace />
      } />

      <Route path="/automations" element={
        <AdminRoute><PortalLayout><AdminAutomations /></PortalLayout></AdminRoute>
      } />

      <Route path="/survey/:token" element={<SurveyView />} />

      {/* Website review — admin console vs client/contractor queue */}
      <Route path="/website-review" element={
        <ProtectedRoute>
          <PortalLayout>
            {viewAsAdmin ? <AdminWebsiteReviews /> : <WebsiteReviews />}
          </PortalLayout>
        </ProtectedRoute>
      } />

      {/* Reviews (redesign, Phase 4): one inbox, and a full-screen workspace
          per asset — no sidebar, the whole width given to the work. */}
      <Route path="/reviews-inbox" element={
        <AdminRoute><PortalLayout><ReviewsInbox /></PortalLayout></AdminRoute>
      } />
      <Route path="/reviews-inbox/web/:id" element={<AdminRoute><ErrorBoundary><ReviewWebWorkspace /></ErrorBoundary></AdminRoute>} />
      <Route path="/reviews-inbox/doc/:id" element={<AdminRoute><ErrorBoundary><ReviewDocWorkspace /></ErrorBoundary></AdminRoute>} />
      <Route path="/reviews-inbox/video/:id" element={<AdminRoute><ErrorBoundary><ReviewVideoWorkspace /></ErrorBoundary></AdminRoute>} />
      {/* The client's own view of a shared video: Team + client notes only. */}
      <Route path="/review/video/:id" element={
        <ProtectedRoute><ErrorBoundary><ReviewVideoWorkspace asClient={!viewAsAdmin} /></ErrorBoundary></ProtectedRoute>
      } />

      {/* Share link — no login, no layout chrome */}
      <Route path="/wr/:token" element={<WebsiteReviewShare />} />
      <Route path="/dr/:token" element={<DocumentReviewShare />} />
      <Route path="/vr/:token" element={<VideoReviewShare />} />

      {/* "All clients" lives at /admin; /clients is what people type and what
          older links used, so it goes there with its query intact. */}
      <Route path="/clients" element={<AdminRoute><ClientsAlias /></AdminRoute>} />

      {/* An unknown address says so, instead of quietly dropping you on the
          dashboard as if you had been thrown out. */}
      <Route path="*" element={
        <ProtectedRoute><PortalLayout><NotFound /></PortalLayout></ProtectedRoute>
      } />
    </Routes>
    </Suspense>
  )
}

export default function App() {
  useEffect(() => {
    supabase.from('app_settings').select('key, value')
      .in('key', ['brand_color_primary', 'brand_color_navy'])
      .then(({ data }) => {
        if (!data) return
        data.forEach(({ key, value }) => {
          if (key === 'brand_color_primary' && value) document.documentElement.style.setProperty('--red', value)
          // --brand-custom, not --navy. An inline property on documentElement
          // beats every stylesheet rule, so writing --navy here pinned the ink
          // colour dark and made every heading invisible in dark mode.
          if (key === 'brand_color_navy' && value) document.documentElement.style.setProperty('--brand-custom', value)
        })
      })
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
